import { Component, type ErrorInfo, type ReactNode } from 'react';

type ErrorBoundaryProps = {
  children: ReactNode;
};

type ErrorBoundaryState = {
  error: Error | null;
  componentStack: string;
};

export class AppErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public state: ErrorBoundaryState = {
    error: null,
    componentStack: '',
  };

  public static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      error,
      componentStack: '',
    };
  }

  public componentDidCatch(error: Error, info: ErrorInfo): void {
    this.setState({ componentStack: info.componentStack ?? '' });

    console.error('React error boundary caught an error:', {
      message: error.message,
      stack: error.stack,
      componentStack: info.componentStack,
    });
  }

  private resetErrorBoundary = (): void => {
    this.setState({
      error: null,
      componentStack: '',
    });
  };

  public render(): ReactNode {
    const { error, componentStack } = this.state;

    if (!error) {
      return this.props.children;
    }

    return (
      <div className="min-h-screen bg-slate-950 px-4 py-8 text-slate-100">
        <div className="mx-auto w-full max-w-5xl space-y-6 rounded-xl border border-slate-800 bg-slate-900/90 p-6 shadow-2xl">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-red-300">Application error</p>
            <h1 className="mt-2 text-2xl font-bold text-red-200">Something crashed in the React tree.</h1>
            <p className="mt-2 text-sm text-slate-300">Review the logs below to debug the issue.</p>
          </div>

          <div className="space-y-3">
            <h2 className="text-sm font-semibold uppercase tracking-[0.12em] text-slate-300">Error message</h2>
            <pre className="overflow-x-auto rounded-md border border-slate-700 bg-slate-950 p-4 text-xs leading-6 text-red-200">
              {error.name}: {error.message}
            </pre>
          </div>

          <div className="space-y-3">
            <h2 className="text-sm font-semibold uppercase tracking-[0.12em] text-slate-300">JavaScript stack trace</h2>
            <pre className="max-h-64 overflow-auto rounded-md border border-slate-700 bg-slate-950 p-4 text-xs leading-6 text-slate-200">
              {error.stack ?? 'No stack trace available.'}
            </pre>
          </div>

          <div className="space-y-3">
            <h2 className="text-sm font-semibold uppercase tracking-[0.12em] text-slate-300">React component stack</h2>
            <pre className="max-h-64 overflow-auto rounded-md border border-slate-700 bg-slate-950 p-4 text-xs leading-6 text-slate-200">
              {componentStack || 'No component stack available.'}
            </pre>
          </div>

          <button
            type="button"
            onClick={this.resetErrorBoundary}
            className="rounded-md bg-red-500 px-4 py-2 text-sm font-semibold text-red-50 transition hover:bg-red-400"
          >
            Try to recover
          </button>
        </div>
      </div>
    );
  }
}
